import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Button,
  Flex,
  TextField,
  TextAreaField,
  SelectField,
  Text,
  Label,
  Message,
} from "@aws-amplify/ui-react";
import FlexFormContainer from "../common/FlexFormContainer";
import { useForm, Controller } from "react-hook-form";
import { useMedicalConditionPostMutation } from "./api/useMedicalConditionMutation";
import { formatDate } from "../common/useDateFunctions";
import DatePicker from "rsuite/DatePicker";
import { zodResolver } from "@hookform/resolvers/zod";
import { useUserQuery } from "../common/useUserQuery";
import { postMedicalConditionSchema } from "./validations/medicalConditions/postValidation";
import { TagsInput } from "react-tag-input-component";

export default function MedicalConditionAddPage() {
  const navigate = useNavigate();
  const { data: userQueryData } = useUserQuery();
  const [showDateInput, setShowDateInput] = React.useState(false);

  const {
    mutate: postMedicalConditionMutate,
    isLoading: postIsLoading,
    isSuccess: postIsSuccess,
    error: postError,
  } = useMedicalConditionPostMutation();

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm({
    defaultValues: {
      diagnosisName: "",
      diagnosisDescription: "",
      tags: [],
      medicalConditionStage: "",
      startDate: "",
    },
    resolver: zodResolver(postMedicalConditionSchema),
  });

  useEffect(() => {
    if (postIsSuccess) {
      navigate("/user/hr/medical-problems/");
    }
  }, [postIsSuccess]);

  const handleOnSubmit = (values) => {
    try {
      const formatStartDate = !!values?.startDate
        ? formatDate(new Date(values.startDate))
        : null;

      let mcParams = {
        patientUserId: userQueryData?.patientUserId,
        diagnosisName: values.diagnosisName,
        diagnosisDescription: values.diagnosisDescription,
        tags: values.tags,
        medicalConditionStage: values.medicalConditionStage,
        startDate: formatStartDate,
      };

      postMedicalConditionMutate(mcParams);
    } catch (error) {
      console.log("Error : ", error);
    }
  };

  return (
    <>
      {postIsLoading && (
        <Flex direction={"column"} margin={15}>
          <Message isDismissible variation="filled" colorTheme="info">
            Saving in progress. Please wait...
          </Message>
        </Flex>
      )}
      {postError && (
        <Flex direction={"column"} margin={15}>
          <Message isDismissible variation="filled" colorTheme="error">
            There was an error saving your data. Please try again.
          </Message>
        </Flex>
      )}

      <form onSubmit={handleSubmit(handleOnSubmit)}>
        <FlexFormContainer headerTitle={"Medical Condition"}>
          <Controller
            name="diagnosisName"
            control={control}
            render={({ field }) => (
              <TextField
                minWidth="300px"
                label="Medical Condition Name"
                {...field}
                value={field.value}
                errorMessage={errors.diagnosisName?.message}
                hasError={!!errors.diagnosisName}
              />
            )}
          />
          <Controller
            name="diagnosisDescription"
            control={control}
            render={({ field }) => (
              <TextAreaField
                minWidth="300px"
                label="Medical Condition Description"
                {...field}
                value={field.value}
                errorMessage={errors.diagnosisDescription?.message}
                hasError={!!errors.diagnosisDescription}
              />
            )}
          />
          <Flex direction={"column"} gap={6}>
            <Label htmlFor="tags">Add Symptoms</Label>
            <Controller
              name="tags"
              control={control}
              render={({ field }) => {
                return (
                  <TagsInput
                    value={field?.value || []}
                    onChange={(tags) => field.onChange(tags)}
                  />
                );
              }}
            />
          </Flex>

          <Controller
            name="medicalConditionStage"
            control={control}
            render={({ field }) => (
              <SelectField
                minWidth="300px"
                label="Medical Condition Stage"
                placeholder="Select"
                {...field}
                value={field.value || ""}
                onChange={(e) => {
                  const value = e.target.value;
                  if (value === "DIAGNOSED") {
                    setShowDateInput(true);
                  } else {
                    setShowDateInput(false);
                    setValue("startDate", "");
                  }
                  field.onChange(value);
                }}
                options={["DIAGNOSED", "POTENTIAL", "PRESUMED", "RULED OUT"]}
                errorMessage={errors.medicalConditionStage?.message}
                hasError={!!errors.medicalConditionStage}
              ></SelectField>
            )}
          />
          {showDateInput && (
            <>
              <Label htmlFor="startDate">Start Date</Label>
              <Controller
                name="startDate"
                control={control}
                render={({ field }) => (
                  <>
                    <DatePicker
                      id={field.name}
                      onChange={(value) => field.onChange(value)}
                      value={field.value}
                      format="yyyy-MM-dd"
                      oneTap
                      size="lg"
                      style={{ width: "300px" }}
                    />
                    {!!errors.symptomStartDate && (
                      <Text
                        variation="error"
                        as="p"
                        lineHeight="0.5em"
                        fontWeight={400}
                        fontSize="0.8em"
                        fontStyle="normal"
                        textDecoration="none"
                        width="30vw"
                        style={{ marginLeft: "10px" }}
                      >
                        {errors.symptomStartDate.message}
                      </Text>
                    )}
                  </>
                )}
              />
            </>
          )}

          <Flex gap="12" direction="column">
            <Button variation="primary" type="submit" disabled={postIsLoading}>
              Save
            </Button>
          </Flex>
        </FlexFormContainer>
      </form>
    </>
  );
}
